import { ApiEntityModel } from '../../_foundation/models/base.models';
import { PtLuxonDate } from '../projects/date.models';

export const MAX_FIELD_POSITIONING_LEGS = 6;

export class FieldPositioningEditor extends ApiEntityModel<number> {
  startDate: PtLuxonDate;
  showStartDate: string;
  startDay: number;

  endDate: PtLuxonDate;
  endDay: number;
  showEndDate: string;

  vesselName: string;
  vesselId: number;

  locationName: string;
  locationId: number;

  projectName: string;
  projectId: number;

  clientName: string;
  activity: string;

  pinnedTime: PtLuxonDate;
  // pinnedTimeOffset: number;
  windSpeed: number | null;
  windDirection: number | null;
  seaHeight: number | null;

  // these 3 are now jacking v1 only
  seaDirection: number | null;
  tideRate: number | null;
  tideDirection: number | null;
  // replaces the above sea/tide fields - jacking v2 only
  currentRate: number | null;
  currentDirection: number | null;

  heading: number | null;
  requiredDistanceAsset: number | null;
  actualDistanceAsset: number | null;
  legInfo: FieldPositioningLegSummaryEditor = new FieldPositioningLegSummaryEditor();

  // only one of those set based on jacking v1/v2
  jackingActivityEntityId: number | null;
  jackingFormInstanceId: number | null;

  localJackingActivityGuid: string;
  confirmed: boolean;
  lastModifiedBy: string;
  lastModifiedDate: PtLuxonDate;
  completionNotes: string;

  // Non-API Properties
  showPinnedTime: string;
  showPinnedTimeFull: string;
  showLastModifiedDate: string;
  status: string;

  // for showing loading icons when download
  portraitLoading: boolean;
  landscapeLoading: boolean;
}

export class FieldPositioningLegSummaryEditor extends ApiEntityModel<number> {
  legs: FieldPositioningLegSummaryPerLegEditor[] = [];
}

export class FieldPositioningLegSummaryPerLegEditor extends ApiEntityModel<number> {
  legNumber: number;
  easting: number | null;
  northing: number | null;
  penetration: number | null;
  finalLegLoad: number | null;
  finalLegExt: number | null;
}

export enum DirectionEnum {
  wind,
  heading,
  tide,
  sea
}

export function createFieldPositioningLeg(legNumber: number): FieldPositioningLegSummaryPerLegEditor {
  const leg = new FieldPositioningLegSummaryPerLegEditor();
  leg.legNumber = legNumber;
  leg.easting = null;
  leg.northing = null;
  leg.penetration = null;
  leg.finalLegLoad = null;
  leg.finalLegExt = null;
  return leg;
}

export function ensureFieldPositioningLeg(
  summary: FieldPositioningLegSummaryEditor,
  legNumber: number
): FieldPositioningLegSummaryPerLegEditor {
  if (!summary.legs) {
    summary.legs = [];
  }

  let leg = summary.legs.find(x => x.legNumber === legNumber);
  if (!leg) {
    leg = createFieldPositioningLeg(legNumber);
    summary.legs.push(leg);
    summary.legs.sort((a, b) => a.legNumber - b.legNumber);
  }

  return leg;
}

export function normalizeFieldPositioningLegSummary(
  summary: FieldPositioningLegSummaryEditor,
  maxLegs: number = MAX_FIELD_POSITIONING_LEGS
): FieldPositioningLegSummaryEditor {
  if (!summary) {
    return new FieldPositioningLegSummaryEditor();
  }

  const normalizedLegs: FieldPositioningLegSummaryPerLegEditor[] = Array.isArray(summary.legs)
    ? summary.legs.map(leg => {
      const normalizedLeg = Object.assign(createFieldPositioningLeg(leg?.legNumber), leg);
      if (!normalizedLeg.legNumber) {
        normalizedLeg.legNumber = 1;
      }
      return normalizedLeg;
    })
    : [];

  for (let legNumber = 1; legNumber <= Math.min(4, maxLegs); legNumber++) {
    const legacyLeg = summary[`leg${legNumber}`];
    if (!legacyLeg) {
      continue;
    }

    const existing = normalizedLegs.find(x => x.legNumber === legNumber);
    if (existing) {
      Object.assign(existing, legacyLeg, { legNumber });
    } else {
      normalizedLegs.push(Object.assign(createFieldPositioningLeg(legNumber), legacyLeg, { legNumber }));
    }

    delete summary[`leg${legNumber}`];
  }

  summary.legs = normalizedLegs
    .filter(leg => !!leg && leg.legNumber >= 1 && leg.legNumber <= maxLegs)
    .sort((a, b) => a.legNumber - b.legNumber);

  return summary;
}



// WEBPACK FOOTER //
// ./src/models/field-positioning/field-positioning.models.ts